import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { environment } from '~env/environment';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MediaObserver } from '@angular/flex-layout';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ExponeaService } from '~shared/services/exponea.service';
import { legalLink } from '~shared/constants/country-code.enum';
import { DISCOUNT_CODE_EXTERNAL_LINKS, LEGAL_JURIDISCH_LINK_NL, NEWSLETTER_PAGE_SOURCE } from '~shared/constants';
import { SafeHtml } from '@angular/platform-browser';
import { VoucherType } from '~shared/models';
import { getCountryDomainCode } from '~server/utils/country-detection.util';
import { PlpVariantRestyleService } from '~shared/services/plp-variant-restyle/plp-variant-restyle.service';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { GATrackingEventType, GaTrackingEventAction, GaTrackingEventName } from '~shared/modules/ga-tracking/constants';

const formSubmitTimer = 1000;
const resendSubmitTimer = 3000;

@Component({
  selector: 'app-newsletter',
  templateUrl: './newsletter.component.html',
  styleUrls: ['./newsletter.component.scss'],
})
export class NewsletterComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() source: NEWSLETTER_PAGE_SOURCE;
  @Input() isSmallSize?: boolean;
  @Input() newsLetterTitle?: string;
  @Input() newsLetterVoucherType?: VoucherType;
  @Input() newsLetterAmount?: string;
  @Input() isSubsidiaryAccount?: boolean;
  @Input() NLDiscount?: string;

  newsLetterImage: string;
  newsLetterImageMobile: string;
  legalLink: string;
  public form: FormGroup;
  destroySubject = new Subject();
  isMobile = false;
  isFormSubmitted = false;
  isLoading = false;
  resend = false;
  pageSlug = '';
  market: string;
  isPLP: boolean;
  isNewPLPVariantRestyle$: Observable<boolean>;

  constructor(
    private fb: FormBuilder,
    private mediaObserver: MediaObserver,
    private exponeaService: ExponeaService,
    private gtmDatalayerService: GtmDatalayerService,
    private cd: ChangeDetectorRef,
    private plpVariantRestyleService: PlpVariantRestyleService
  ) {
    this.market = getCountryDomainCode();
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
    this.newsLetterImage = `${environment.cdnBaseUrl()}/images/desktop-newsletter-character.png`;
    this.newsLetterImageMobile = `${environment.cdnBaseUrl()}/images/mobile-newsletter-character.png`;
    this.legalLink = legalLink[this.market];
  }

  ngOnInit(): void {
    this.mediaObserver
      .asObservable()
      .pipe(takeUntil(this.destroySubject))
      .subscribe(() => {
        this.isMobile = this.mediaObserver.isActive('mm-mobile') || this.mediaObserver.isActive('mm-tablet');
      });
    this.pageSlug = this.getPageSlug();
    this.isPLPVariantRestyle();
  }
  isPLPVariantRestyle() {
    this.isNewPLPVariantRestyle$ = this.plpVariantRestyleService.isNewPLPVariantRestyle();
    this.isPLP = this.plpVariantRestyleService.isPLP();
  }

  ngAfterViewInit(): void {
    this.initializeNewsletterView();
  }

  onSubmit() {
    if (this.form.valid) {
      this.postNewsletterInfo()
    }
  }

  resendEmail() {
    this.postNewsletterInfo(true)
  }

  private postNewsletterInfo(isResend: boolean = false): void {
    if (isResend) {
      this.resend = true;
    }

    this.isLoading = true;
    this.exponeaService
      .sendNewsletterConsentData(this.form.value.email, this.source, this.pageSlug)
      .toPromise()
      .then(() => {
        this.isFormSubmitted = true;
        this.fireSuscriptionEvent();
      })
      .catch(() => {
        this.isFormSubmitted = false;
        if (isResend) {
          this.resend = false;
        }
      })
      .finally(() => {
        this.isLoading = false;
        this.cd.detectChanges();
      })
  }

  getPageSlug(): string {
    return window.location.pathname.split('/').pop() || '';
  }

  getPromoText(promoText: string, promoLinkText: string): string {
    if (promoText && promoLinkText && DISCOUNT_CODE_EXTERNAL_LINKS[this.market]) {
      const promoLink = `<a href=${DISCOUNT_CODE_EXTERNAL_LINKS[this.market]}>${promoLinkText}</a>`;
      return `<br>${promoText} ${promoLink}.`;
    }

    return '';
  }

  getNewsletterContent(
    legalText: string,
    policyText: string,
    promoTextTranslation: string,
    promoLinkText: string
  ): SafeHtml {
    const metroLink = `<a href=${LEGAL_JURIDISCH_LINK_NL}>METRO Markets GmbH</a>`;
    const legalLink = `<a href=${this.legalLink}>${policyText}</a>`;
    const promoText = this.getPromoText(promoTextTranslation, promoLinkText);
    return `${
      this.market === 'nl' ? legalText.replace('METRO Markets GmbH', metroLink) : legalText
    } ${legalLink}.${promoText}`;
  }

  initializeNewsletterView(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.NEWSLETTER_VIEWED,
      GA_eventname: GaTrackingEventName.Interaction,
      action: GaTrackingEventAction.NEWSLETTER_VIEWED,
    });
  }

  fireSuscriptionEvent(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.NEWSLETTER_SUSCRIPTION,
      GA_eventname: GaTrackingEventName.Interaction,
      action: GaTrackingEventAction.NEWSLETTER_SUSCRIPTION,
      label: this.source,
    });
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
